import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import api from "../utils/api";
import { formatUkDateTime } from "../utils/dateLocale";

const CronSchedules = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingSlug, setSavingSlug] = useState(null);

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/api/cron-schedules");
      setSchedules(response.data || []);
      setEdits({});
    } catch (err) {
      console.error("Failed to fetch cron schedules:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load cron schedules.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleEdit = (slug, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [slug]: { ...(prev[slug] || {}), [field]: value },
    }));
  };

  const getDisplayValue = (schedule, slug, field) => {
    if (edits[slug] && edits[slug][field] !== undefined) {
      return edits[slug][field];
    }
    if (field === "cronExpression") return schedule.cronExpression;
    if (field === "enabled") return schedule.enabled;
    return schedule[field];
  };

  const handleSave = async (slug) => {
    const patch = edits[slug];
    if (!patch || Object.keys(patch).length === 0) return;
    try {
      setSavingSlug(slug);
      setError("");
      setSuccess("");
      await api.put(`/api/cron-schedules/${slug}`, patch);
      setSuccess(
        "Schedule updated. Restart the server for changes to take effect.",
      );
      setEdits((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      await fetchSchedules();
    } catch (err) {
      console.error("Failed to update schedule:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to update schedule.",
      );
    } finally {
      setSavingSlug(null);
    }
  };

  const hasEdits = (slug) => edits[slug] && Object.keys(edits[slug]).length > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Schedules are stored in the database. Changes take effect after the
            server is restarted.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Frequency (cron expression)</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Last run</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Config updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.slug}>
                    <TableCell>{schedule.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {schedule.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. 0 9 * * *"
                        value={getDisplayValue(
                          schedule,
                          schedule.slug,
                          "cronExpression",
                        )}
                        onChange={(e) =>
                          handleEdit(
                            schedule.slug,
                            "cronExpression",
                            e.target.value.trim() || schedule.cronExpression,
                          )
                        }
                        helperText="Cron format: minute hour day month weekday (UTC). e.g. 0 9 * * * = 9 AM daily"
                        sx={{ minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={getDisplayValue(
                              schedule,
                              schedule.slug,
                              "enabled",
                            )}
                            onChange={(e) =>
                              handleEdit(
                                schedule.slug,
                                "enabled",
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label={
                          getDisplayValue(schedule, schedule.slug, "enabled")
                            ? "On"
                            : "Off"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {schedule.lastRunAt
                          ? formatUkDateTime(schedule.lastRunAt)
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {schedule.lastRunDurationMs != null
                          ? `${(schedule.lastRunDurationMs / 1000).toFixed(2)}s`
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {schedule.updatedAt
                          ? formatUkDateTime(schedule.updatedAt)
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSave(schedule.slug)}
                        disabled={
                          !hasEdits(schedule.slug) ||
                          savingSlug === schedule.slug
                        }
                      >
                        {savingSlug === schedule.slug ? "Saving..." : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CronSchedules;
